import { useEffect, useRef, useState } from 'react';
import { CheckValidateValue } from '../components/common/RayFormValidation';

// 상세 화면 한 번에 저장(IMP-484 #592): 입력은 draft 에만 쌓고, [저장] 때 편집 시작 뒤 바뀐 칸만 한 요청으로 보낸다.
//   toDraft(source)  서버 값 → 입력 draft.
//   save(changed)    저장 액션. 성공 때만 true 를 돌려준다(저장 액션 반환 계약) — 그 밖이면 편집과 draft 를 유지한다.
//   entry 'button'   [수정]으로 편집을 연다. editable 은 편집 권한이고, 꺼지면 [취소]와 같이 닫힌다.
//   entry 'header'   머리글 잠금/연필(editable)이 곧 편집 시작이다. 풀리는 순간 스냅숏을 잡고, 잠기면 서버 값으로 되돌린다.
//                    저장 성공·[취소] 때 onClose(머리글 잠금)를 부른다. 없으면 연 채로 새 스냅숏에서 이어 간다.
//   recordKey        편집 대상 레코드 식별자(기본 source._id). 바뀌면 저장 중이어도 앞 레코드의 입력을 버린다.
// 편집 중에는 source 가 바뀌어도(다른 즉시 저장 응답 등) draft 를 덮지 않는다 — 두 칸을 함께 고치면 하나가 되돌아가던 원인.
const same = (a, b) => {
	const x = a ?? '';
	const y = b ?? '';
	if (typeof x === 'object' || typeof y === 'object') return JSON.stringify(x) === JSON.stringify(y);
	return x == y;
};

export const useBatchEdit = ({ source, toDraft, save, editable = true, entry = 'button', onClose = null, recordKey = source?._id }) => {
	const header = entry === 'header';
	const [editingState, setEditingState] = useState(false);
	const editing = header ? !!editable : editingState;
	const [saving, setSaving] = useState(false);
	const [errors, setErrors] = useState([]);
	const [formKey, setFormKey] = useState(0);
	const startRef = useRef({});
	// 저장 성공 뒤에도 source 가 같은 객체로 남는 화면(열 때 찍은 사본을 그리는 화면)은 저장한 값을 서버 값으로 본다.
	const savedRef = useRef(null);
	const aliveRef = useRef(true);

	// 마지막 서버 값 → draft. 동기화 effect, [취소], 편집 시작 스냅숏이 같이 쓴다.
	const serverDraft = () => (savedRef.current && savedRef.current.source === source
		? savedRef.current.draft
		: toDraft(source));

	const [draft, setDraft] = useState(() => toDraft(source));

	useEffect(() => {
		aliveRef.current = true;
		return () => { aliveRef.current = false; };
	}, []);

	useEffect(() => {
		if (editing) return;
		if (savedRef.current && savedRef.current.source !== source) savedRef.current = null;
		setDraft(serverDraft());
	}, [source, editing]);

	const resetForm = () => {
		setErrors([]);
		setFormKey(k => k + 1);
	};

	useEffect(() => {
		if (header) {
			if (editable) startRef.current = serverDraft();
			resetForm();
		} else if (!editable && editingState) {
			resetForm();
			setEditingState(false);
		}
	}, [editable]);

	// 목록에서 다른 행을 고르는 등 대상 레코드가 바뀌면, 앞 레코드의 입력이 새 레코드 id 로 저장되지 않게 버린다.
	const recordRef = useRef(recordKey);
	useEffect(() => {
		if (recordRef.current === recordKey) return;
		recordRef.current = recordKey;
		savedRef.current = null;
		const next = toDraft(source);
		startRef.current = next;
		setDraft(next);
		resetForm();
		if (!header) setEditingState(false);
	}, [recordKey]);

	const close = () => {
		if (header) {
			if (onClose) onClose();
			else startRef.current = serverDraft();
		} else {
			setEditingState(false);
		}
	};

	const onChange = (e) => {
		const name = e?.target?.name;
		if (!name) return;
		const value = e.target.value;
		setDraft(prev => ({ ...prev, [name]: value }));
	};

	const setField = (name, value) => setDraft(prev => ({ ...prev, [name]: value }));

	const onEdit = () => {
		startRef.current = draft;
		resetForm();
		setEditingState(true);
	};

	const onCancel = () => {
		if (saving) return;
		setDraft(serverDraft());
		resetForm();
		close();
	};

	// controls: 화면이 RayForm 에 넘기는 칸 정의([{ items: [...] }]). 바뀐 칸만 기존 입력 검증(필수·이메일·전화)을 돈다.
	const onSave = async (controls = []) => {
		if (saving) return;
		const snap = startRef.current || {};
		const changed = {};
		// 비교 대상은 toDraft 의 칸뿐이다 — 즉시 저장 체크박스 등도 onChange 로 draft 에 들어오지만 다시 보내지 않는다.
		Object.keys(toDraft(source)).forEach(k => { if (!same(draft[k], snap[k])) changed[k] = draft[k]; });

		if (Object.keys(changed).length === 0) {
			close();
			return;
		}

		const errs = (controls || [])
			.flatMap(x => x?.items || [])
			.filter(c => c?.name && Object.prototype.hasOwnProperty.call(changed, c.name))
			.map(c => CheckValidateValue({ ...c, val: changed[c.name] }))
			.filter(Boolean);
		if (errs.length > 0) {
			setErrors(errs);
			return;
		}

		setErrors([]);
		setSaving(true);
		const key = recordRef.current;
		try {
			const ok = await save(changed);
			// 응답 전에 대상 레코드가 바뀌었으면 결과로 새 레코드의 편집 상태를 건드리지 않는다.
			if (ok === true && aliveRef.current && recordRef.current === key) {
				savedRef.current = { source, draft };
				startRef.current = draft;
				close();
			}
		} catch (error) {
			console.warn('[useBatchEdit] save failed', { reason: error?.message });
		} finally {
			if (aliveRef.current) setSaving(false);
		}
	};

	return { draft, editing, saving, errors, formKey, onChange, setField, onEdit, onSave, onCancel };
};

export default useBatchEdit;
